import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';

import { type Language, LANGUAGES } from '@/modules/i18n/languages';
import { registerLocaleResources, useTranslation } from '@/modules/i18n/useTranslation';
import { buildLanguagePath, parseLanguagePath, useLanguage } from '@/modules/routing';

import en from './locales/en.json';
import es from './locales/es.json';
import ru from './locales/ru.json';
import { styles } from './style';

registerLocaleResources('LanguagePicker', { en, es, ru });

declare module '@/modules/i18n/namespaces' {
    interface LocaleNamespaces {
        LanguagePicker: typeof en;
    }
}

// Native names, never translated: a visitor stuck in a language they cannot read still recognizes their own
const LANGUAGE_NAMES: Readonly<Record<Language, string>> = {
    en: 'English',
    es: 'Español',
    ru: 'Русский',
};

/**
 * Changes only the language of the URL: the page, its query and hash stay. Each language is a link, so «back»
 * returns the previous one; i18next follows the new URL through LanguageGate.
 */
const LanguagePicker = () => {
    const { t } = useTranslation('LanguagePicker');
    const { pathname, search, hash } = useLocation();
    const current = useLanguage();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const { rest } = parseLanguagePath(pathname);
    const close = () => setAnchor(null);

    return (
        <>
            <Button
                color="inherit"
                sx={styles.button}
                endIcon={<ArrowDropDownIcon/>}
                aria-label={t('label')}
                aria-haspopup="menu"
                onClick={(event) => setAnchor(event.currentTarget)}
            >
                {LANGUAGE_NAMES[current]}
            </Button>
            <Menu anchorEl={anchor} open={anchor !== null} onClose={close}>
                {LANGUAGES.map((lang) => (
                    <MenuItem
                        key={lang}
                        component={RouterLink}
                        to={`${buildLanguagePath(lang, rest)}${search}${hash}`}
                        lang={lang}
                        selected={lang === current}
                        onClick={close}
                    >
                        {LANGUAGE_NAMES[lang]}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default LanguagePicker;
